import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Car, Plus, Tag, RefreshCw, ImageOff } from "lucide-react";
import { getMyVehicles, CustomerVehicle } from "@/Calls/callCustomer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { API_URL } from "@/config";

function VehicleCard({ v }: { v: CustomerVehicle }) {
    const primaryPhoto = v.photos.find((p) => p.is_primary) ?? v.photos[0];
    const photoUrl = primaryPhoto ? `${API_URL.replace("/api", "")}${primaryPhoto.photo_path}` : null;

    const typeBadge = v.trade_type === "FSBO"
        ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">For Sale</Badge>
        : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Trade-In</Badge>;

    const statusBadge = v.trade_status === "ACTIVE"
        ? <Badge variant="outline" className="text-green-600 border-green-300">Active</Badge>
        : <Badge variant="outline" className="text-muted-foreground">{v.trade_status}</Badge>;

    return (
        <Card className="overflow-hidden">
            <div className="aspect-video bg-muted flex items-center justify-center relative">
                {photoUrl ? (
                    <img src={photoUrl} alt={`${v.trade_year} ${v.trade_make}`} className="object-cover w-full h-full" />
                ) : (
                    <ImageOff className="h-10 w-10 text-muted-foreground/40" />
                )}
                <div className="absolute top-2 left-2 flex gap-1">
                    {typeBadge}
                    {statusBadge}
                </div>
            </div>
            <div className="p-4">
                <p className="font-medium">
                    {v.trade_year} {v.trade_make} {v.trade_model}
                    {v.trade_trim ? ` ${v.trade_trim}` : ""}
                </p>
                <div className="flex gap-3 mt-1 text-sm text-muted-foreground">
                    {v.trade_miles != null && <span>{v.trade_miles.toLocaleString()} mi</span>}
                    {v.trade_color && <span>· {v.trade_color}</span>}
                    {v.trade_condition && <span>· {v.trade_condition}</span>}
                </div>
                {v.trade_price && (
                    <p className="mt-2 text-lg font-semibold text-indigo-600">
                        ${Number(v.trade_price).toLocaleString()}
                    </p>
                )}
                {v.trade_description && (
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{v.trade_description}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                    <Link to={`/customer/vehicles/${v.trade_id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full">View / Edit</Button>
                    </Link>
                    <Link to={`/customer/vehicles/${v.trade_id}/photos`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full">
                            Photos ({v.photos.length})
                        </Button>
                    </Link>
                    <Link
                        to={`/auctions/marketplace?post=1&tradeId=${v.trade_id}`}
                        className="flex-1"
                    >
                        <Button variant="outline" size="sm" className="w-full text-violet-600 border-violet-300 hover:bg-violet-50">
                            Post to Auction
                        </Button>
                    </Link>
                </div>
            </div>
        </Card>
    );
}

export default function CustomerVehiclesPage() {
    const [vehicles, setVehicles] = useState<CustomerVehicle[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<"ALL" | "FSBO" | "TRADE_IN">("ALL");

    useEffect(() => {
        getMyVehicles()
            .then(setVehicles)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const filtered = filter === "ALL" ? vehicles : vehicles.filter((v) => v.trade_type === filter);

    return (
        <div className="space-y-6 max-w-5xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-light uppercase tracking-wide">My Vehicles</h1>
                    <p className="text-muted-foreground text-sm mt-1">{vehicles.length} vehicle{vehicles.length !== 1 ? "s" : ""} listed</p>
                </div>
                <Link to="/customer/vehicles/upload">
                    <Button className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Vehicle
                    </Button>
                </Link>
            </div>

            {/* Filter tabs */}
            <div className="flex gap-2">
                {(["ALL", "FSBO", "TRADE_IN"] as const).map((f) => (
                    <button
                        key={f}
                        onClick={() => setFilter(f)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            filter === f
                                ? "bg-indigo-600 text-white"
                                : "bg-muted text-muted-foreground hover:bg-muted/80"
                        }`}
                    >
                        {f === "ALL" ? "All" : f === "FSBO" ? "For Sale" : "Trade-Ins"}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <Card key={i} className="h-64 animate-pulse bg-muted" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <Card className="p-12 text-center border-dashed">
                    <Car className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <p className="font-medium">No vehicles{filter !== "ALL" ? ` in ${filter === "FSBO" ? "For Sale" : "Trade-In"}` : ""}</p>
                    <p className="text-sm text-muted-foreground mt-1 mb-5">
                        Add your first vehicle to get started.
                    </p>
                    <Link to="/customer/vehicles/upload">
                        <Button className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white">
                            <Plus className="h-4 w-4 mr-2" />
                            List a Vehicle
                        </Button>
                    </Link>
                </Card>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map((v) => (
                        <VehicleCard key={v.trade_id} v={v} />
                    ))}
                </div>
            )}
        </div>
    );
}
